import { FC } from 'react';

import { DrawerSection } from 'components/drawerSections/DrawerSections';
import InputField from 'components/fieldComponents/raaInputField/RaaInputField';
import TextAreaField from 'components/fieldComponents/raaTextAreaField/RaaTextAreaField';
import { TextButton } from 'components/sharedElements/sharedElements';
import { SammansattFormValues } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import { BebyggelseobjektUtkast } from 'models';

import { BebyggelseobjektgruppEnum, SkyddstypEnum } from 'enums';

import classNames from 'classnames';
import { FieldArray, useFormikContext } from 'formik';
import { v4 as uuid } from 'uuid';

interface SkyddsbestammelseSectionProps {
  utkastData: BebyggelseobjektUtkast;
}

const SkyddsbestammelseSection: FC<SkyddsbestammelseSectionProps> = ({ utkastData }) => {
  const { values, errors } = useFormikContext<SammansattFormValues>();

  return (
    <DrawerSection>
      {utkastData.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT && (
        <>
          <h2 className="tw-heading-md tw-mb-4">Skyddsbestämmelser</h2>

          <FieldArray
            name="skyddsbestammelser"
            validateOnChange={false}
            render={(arrayHelpers) => (
              <div className="tw-flex tw-flex-col tw-gap-4">
                {values.skyddsbestammelser.map((_sb, index) => (
                  <fieldset key={index} data-testid="skyddsbestammelse">
                    <legend className="tw-sr-only">Skyddsbestämmelse {index + 1}</legend>

                    <div className="tw-flex tw-items-end">
                      <div className="tw-h-full tw-flex-2">
                        <InputField
                          name={`skyddsbestammelser.${index}.rubrik`}
                          label="Rubrik"
                          shouldValidateForm={true}
                        />
                      </div>

                      <div
                        className={classNames('tw-flex tw-flex-1 tw-justify-end', {
                          'tw-mb-0 tw-self-center': errors.skyddsbestammelser?.[index],
                          'tw-mb-2': !errors.skyddsbestammelser?.[index]
                        })}
                      >
                        <button
                          className={classNames(
                            'raa-icon raa-icon-arrow-down tw-w-8 tw-text-size-1.625 tw-text-raa-orange',
                            {
                              '!tw-text-raa-gray-4': index === values.skyddsbestammelser.length - 1
                            }
                          )}
                          aria-label="Flytta ner skyddsbestämmelse"
                          data-testid="move-skyddsbestammelse-down"
                          type="button"
                          disabled={index === values.skyddsbestammelser.length - 1}
                          onClick={() => arrayHelpers.move(index, index + 1)}
                        />

                        <button
                          className={classNames(
                            'raa-icon raa-icon-arrow-up tw-w-8 tw-text-size-1.625 tw-text-raa-orange',
                            {
                              '!tw-text-raa-gray-4': index === 0
                            }
                          )}
                          aria-label="Flytta upp skyddsbestämmelse"
                          data-testid="move-skyddsbestammelse-up"
                          type="button"
                          disabled={index === 0}
                          onClick={() => arrayHelpers.move(index, index - 1)}
                        />

                        <button
                          className={classNames('raa-icon raa-icon-delete tw-w-8 tw-text-raa-orange', {
                            '!tw-text-raa-gray-4':
                              (utkastData.skydd.skyddstyp === SkyddstypEnum.STATLIGT_BYGGNADSMINNE ||
                                utkastData.skydd.skyddstyp === SkyddstypEnum.BYGGNADSMINNE) &&
                              values.skyddsbestammelser.length === 1 &&
                              index === 0
                          })}
                          aria-label="Ta bort skyddsbestämmelse"
                          data-testid="delete-skyddsbestammelse"
                          disabled={
                            (utkastData.skydd.skyddstyp === SkyddstypEnum.STATLIGT_BYGGNADSMINNE ||
                              utkastData.skydd.skyddstyp === SkyddstypEnum.BYGGNADSMINNE) &&
                            values.skyddsbestammelser.length === 1 &&
                            index === 0
                          }
                          onClick={() => arrayHelpers.remove(index)}
                          type="button"
                        />
                      </div>
                    </div>

                    <TextAreaField
                      name={`skyddsbestammelser.${index}.skyddsbestammelse`}
                      label="Skyddsbestämmelse"
                      classString="raa-textarea tw-h-48 tw-mb-4"
                    />
                  </fieldset>
                ))}

                <div>
                  <TextButton
                    icon="raa-icon-add"
                    onClick={() =>
                      arrayHelpers.push({
                        attribut_id: uuid(),
                        rubrik: '',
                        skyddsbestammelse: ''
                      })
                    }
                  >
                    Lägg till skyddsbestämmelse
                  </TextButton>
                </div>
              </div>
            )}
          ></FieldArray>
        </>
      )}
    </DrawerSection>
  );
};

export default SkyddsbestammelseSection;
